import { Paper, Text, Group, createStyles, Box, ScrollArea, Table, Avatar, LoadingOverlay } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useDasboardApi from '../../hooks/useDashboardApi';
import TimeSelector from './card-components/TimeSelector';
import BenefitPerformanceFilterSelector from './selectors/BenefitPerformanceFilterSelector';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: '20px 0 10px'
	},
	text: {
		fontSize: 36,
		lineHeight: '42px',
		fontWeight: 300,
	},
	cardTitle: {
		fontWeight: 600,
		fontSize: 16,
		lineHeight: '20px',
		color: theme.colorScheme === 'light' ? '#4A4A4A' : '#C1C2C5',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.xs}`,
	},
	th: {
		padding: '0 !important',
	},
}));

export default function BenefitPerformance() {

	// State and hooks
	const { classes } = useStyles();
	const { getBenefitPerformance } = useDasboardApi();
	const [selectedRange, setSelectedRange] = useState('total');
	const [range, setRange] = useState([null, null]);
	const benefitPerformance = useSelector(state => state.dashboardData.benefitPerformance);
	const [filteredData, setFilteredData] = useState([]);
	const [filterOptions, setFilterOptions] = useState([]);
	const [filters, setFilters] = useState([]);
	const [performanceOrder, setPerformanceOrder] = useState('');
	const [visible, setVisible] = useState(false);
	const [statusFilter, setStatusFilter] = useState('true, false');
	const [userFilter, setUserFilter] = useState('1, 2');
	const [levelFilter, setLevelFilter] = useState('1, 2, 3');

	console.log(benefitPerformance)

	// On load
	useEffect(() => {
		async function fetchData() {
			await getBenefitPerformance('', '', statusFilter, userFilter, levelFilter);
		}
		fetchData();
		// eslint-disable-next-line
	}, []);

	// Listen to all filter changes and reload
	useEffect(() => {
		reload();
		// eslint-disable-next-line
	}, [statusFilter, userFilter, levelFilter]);

	// On data load
	useEffect(() => {

		// If not null
		if (benefitPerformance) {

			// Get unique filters
			const uniqueFilters = benefitPerformance.reduce((acc, curr) => {
				acc.categories = acc.categories.includes(curr.category) ? acc.categories : [...acc.categories, curr.category];
				return acc;
			}, { categories: [] });
			setFilterOptions(uniqueFilters);

		}

		// Set data 1:1
		setFilteredData(benefitPerformance);

	}, [benefitPerformance]);

	// Listen to selected range changes
	useEffect(() => {

		// Only if initial data has loaded
		if (benefitPerformance !== null) reload();

		// eslint-disable-next-line
	}, [selectedRange]);

	// Reload with new values
	const reload = async () => {

		// Set loader
		setVisible(true);

		// Define date variables
		let endDate = new Date();
		let startDate = new Date();

		// Check date range to apply
		switch (selectedRange) {
			case '30d':
				startDate.setDate(endDate.getDate() - 30);
				break;
			case '7d':
				startDate.setDate(endDate.getDate() - 7);
				break;
			case 'total':
				// Remove both dates
				endDate = '';
				startDate = '';
				break;
			case 'custom':
				// Assign custom range
				endDate = range[1];
				startDate = range[0];
				break;
			default:
				// Remove both dates
				endDate = '';
				startDate = '';
				break;
		}

		// Get information
		if (selectedRange) await getBenefitPerformance(startDate, endDate, statusFilter, userFilter, levelFilter);

		// Remove loader
		setVisible(false);

	}

	// Order by performance
	const orderByPerformance = (order = 'more') => {

		// Order and update
		const newData = filteredData.slice().sort((a, b) => a.performance - b.performance);
		setPerformanceOrder(order);
		setFilteredData(newData);

	}

	// Build rows
	const rows = filteredData?.map((element) => (
		<tr key={Math.random()}>
			<td>
				{element.benefitId}.
			</td>
			<td>
				<Avatar
					src={`${element.partner.logo}`}
					size="lg"
					color="secondaryPurple"
					variant="light"
				/>
			</td>
			<td>
				<Group spacing={8}>
					{/* Avatar */}
					<Avatar
						src={element.image}
						size="lg"
						color="secondaryPurple"
						variant="light"
					/>
					{/* Name */}
					<Text>
						{element.name}
					</Text>
				</Group>
			</td>
			<td>
				{element.performance}
			</td>
		</tr>
	));

	// Reset filters
	const resetFilters = () => {

		// Reset state filters
		setPerformanceOrder('');
		setFilteredData(benefitPerformance);
		setFilters([]);
		setStatusFilter('true, false');
		setUserFilter('1, 2');
		setLevelFilter('1, 2, 3');

	}

	// Category filters
	useEffect(() => {

		// If there are filters
		if (filters.length > 0) {

			// Filter
			setFilteredData(benefitPerformance.filter(el => filters.includes(el.category)));

		} else {

			// Reset
			setFilteredData(benefitPerformance);

		}

		// eslint-disable-next-line
	}, [filters]);

	return (
		<Paper
			withBorder
			p={12}
			style={{ position: 'relative' }}
			radius={18}
		>
			{/* Loading Overlay */}
			<LoadingOverlay
				visible={visible}
				overlayBlur={1}
				radius={18}
			/>
			{/* Top */}
			<Group
				position="apart"
				noWrap
			>
				{/* Title */}
				<Text className={classes.cardTitle}>
					Rendimiento
				</Text>
			</Group>
			{/* Time & Filter */}
			<Group
				position="apart"
				mt={8}
				noWrap
				align="center"
			>
				{/* Empty */}
				<TimeSelector
					data={benefitPerformance}
					selectedRange={selectedRange}
					setSelectedRange={setSelectedRange}
					range={range}
					setRange={setRange}
				/>
				{/* Filter */}
				<BenefitPerformanceFilterSelector
					selected={[filters, setFilters]}
					options={filterOptions}
					performanceOrder={performanceOrder}
					orderByPerformance={orderByPerformance}
					resetFilters={resetFilters}
					filterByStatus={setStatusFilter}
					statusFilter={statusFilter}
					filterByUser={setUserFilter}
					userFilter={userFilter}
					filterByLevel={setLevelFilter}
					levelFilter={levelFilter}
				/>
			</Group>
			{/* Content */}
			<Box className={classes.container}>
				{/* Table */}
				<ScrollArea>
					<Table sx={{ flex: 2 }}>
						<tbody>{rows}</tbody>
					</Table>
				</ScrollArea>
			</Box>
		</Paper>
	);

}
